import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DateFormat from '../components/DateFormat';
import LoadingSpinner from '../components/LoadingSpinner';
import HeadingCp from '../components/HeadingCp';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Truncate from '../components/Truncate';
import ModalWindow from '../components/ModalWindow';
import SearchBarCP from '../components/SearchBarCP';

const AuditLogReport = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [notesIdOpen, setNotesIdOpen] = useState(null);
  const [newValueIdOpen, setNewValueIdOpen] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const api = `${apiUrl}/config_system_crud.php`;

  const [employees, setEmployees] = useState([]);
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterEmployeeId, setFilterEmployeeId] = useState(''); // store employee id




  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get(`${apiUrl}/service_crud.php?employee_list=true`, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }
        });
        setEmployees(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Error fetching employees', e);
      }
    };

    fetchEmployees();
  }, [apiUrl]);


  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(api, { params: { getLogs: 1 } });
      const data = Array.isArray(res.data) ? res.data : [];
      setLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      toast.error('Error fetching audit logs', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value === '') {
      setFilteredLogs(logs);
    } else {
      const filtered = logs.filter((log) =>
        (log.username || '').toLowerCase().includes(value.toLowerCase()) ||
        (log.action || '').toLowerCase().includes(value.toLowerCase()) ||
        (log.module_name || '').toLowerCase().includes(value.toLowerCase()) ||
        (log.description || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredLogs(filtered);
    }
  };

  const handleSearchClick = async () => {
    setLoading(true);
    try {
      const response = await axios.post(api, {
        status: 'searchLogs',
        data: { search: searchTerm }
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      toast.error('Search failed', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = async () => {
    if (!dateFrom || !dateTo) {
      toast.error("Please provide both start and end dates.", { position: "top-center" });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(api, {
        status: 'filterLogs',
        data: {
          date_from: dateFrom,
          date_to: dateTo,
          user_id: filterEmployeeId || null // optional
        }
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      setLogs(data);
      setFilteredLogs(data);
    } catch (error) {
      toast.error('Filter failed', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  const handleNotesView = (id) => {
    setNotesIdOpen(id);
  };
 

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
      <HeadingCp label="Audit Log" />
      <SearchBarCP search={searchTerm} handleChange={handleSearchChange} onClick={handleSearchClick} />

      <div className="flex flex-wrap gap-2 my-4 items-center bg-gray-100 p-4 rounded-lg">
        <input
          type="date"
          className="border border-gray-300 rounded-md px-2 py-2"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />

        <span className="text-gray-600">to</span>

        <input
          type="date"
          className="border border-gray-300 rounded-md px-2 py-2"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />

        <select
          value={filterEmployeeId}
          onChange={(e) => setFilterEmployeeId(e.target.value)}
          className="border border-gray-300 rounded-md px-2 py-2"
        >
          <option value="">All Employees</option>
          {employees?.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.first_name} {emp.last_name}
            </option>
          ))}
        </select>

        <button
          onClick={handleDateFilter}
          className="bg-blue-500 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded"
        >
          Filter
        </button>
      </div>
      <br/>

      {loading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : (
        <table className="min-w-full divide-y divide-gray-300 bg-gray-200 border rounded-lg text-xs sm:text-sm">
          <thead>
            <tr className="divide-x divide-gray-400">
              <th className="py-3.5 px-3 text-left font-semibold text-gray-900">Date/Time</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">User</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Action</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Module</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Record ID</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Description</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">Old Values</th>
              <th className="px-3 py-3.5 text-left font-semibold text-gray-900">New Values</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log, index) => (
              <tr key={log.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'} divide-x`}>
                <td className="px-3 py-2 text-gray-800"><DateFormat date={log.event_time} /></td>
                <td className="px-3 py-2 text-gray-800">{log.username || log.user_id}</td>
                <td className="px-3 py-2 text-gray-800">{log.action}</td>
                <td className="px-3 py-2 text-gray-800">{log.module_name}</td>
                <td className="px-3 py-2 text-gray-800">{log.record_id}</td>
                <td className="px-3 py-2 text-gray-800">{log.description}</td>
                <td className="px-3 py-2 text-gray-600 break-all max-w-xs">
                  {/* {log.old_values} */}
                    {log.old_values && 
                      <Truncate text={log.old_values} handleNotesView={() => handleNotesView(log.id)} />
                    }
                    {notesIdOpen === log.id && (
                      <ModalWindow job={log.old_values} closeNotes={() => setNotesIdOpen(null)} activeButton={true} />
                    )}                  
                </td> 
                <td className="px-3 py-2 text-gray-600 break-all max-w-xs">
                  {/* {log.new_values} */}
                   {log.new_values && 
                      <Truncate text={log.new_values} handleNotesView={() => handleNotesView(log.id)} />
                    }
                    {newValueIdOpen === log.id && (
                      <ModalWindow job={log.new_values} closeNotes={() => setNewValueIdOpen(null)} activeButton={true} />
                    )}                   
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AuditLogReport;
